import {
    IconButton,
    IContextualMenuItem,
    ProgressIndicator,
    SelectionMode,
    Spinner,
    Stack,
    Text,
} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import React, {
    CSSProperties,
    FC,
    MutableRefObject,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {useDropzone} from "react-dropzone";
import {useTranslation} from "react-i18next";
import Axios, {AxiosRequestConfig, CancelTokenSource,} from "axios";
import {useBoolean} from "@fluentui/react-hooks";
import {theme} from "..";
import {ApplicationUser, ApplicationUserSavedFileDto, PagedResult, SavedFile,} from "../API";
import {TenantResource} from "../enums/TenantResource";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {UUIDHelper} from "../helpers/UUIDHelper";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {ITenantFormLayout, MultilineTextElement, TitleElement,} from "./OnixElements";
import {GetApiError, ShowApiError} from "./ShowApiError";
import {DownloadHelper, OpenInNewTabHelper} from "../helpers/DownloadHelper";
import {hr} from "../locales/hr";
import {OnixCallout} from "./OnixCallout";
import {ListItemCheckbox} from "./AsCheckMark";
import {TenantList} from "./TenantList";
import {FormAction} from "../types/TenantTypes";

interface IFileUploadComponent {
    additionalCommands?: JSX.Element[];
    title?: keyof typeof hr | string;
    query: ITenantResourceQuery<SavedFile>;
    baseDto: Partial<SavedFile>;
    showDocumentList?: boolean;
    hideTopBar?: boolean;
    loadingCallback?: () => void;
    loadedCallback?: () => void;
    disabled?: boolean;
    translationKey?: keyof typeof hr;
    maxXorY?: number;
    nameOverride?: string;
    prependOriginalFileName?: boolean;
    keepOriginalExtension?: boolean;
    additionalDirectorySegments?: string;
    uniqueClientId?: string;
    filesListIsEmpty?: () => void;
    showUserAuthorization?: boolean;
    allowedActions?: FormAction[];
    className?: string;
    selectionChanged?: (item: Partial<SavedFile> | undefined) => void;
    filesChanged?: (files: SavedFile[]) => void;
    checkAgainstEmailMaxSize?: boolean;
    // showEditRightAfterUpload?: boolean;
}

interface FileWrapper extends File {
    id: string;
    loading: boolean;
    loaded: boolean;
    deleting: boolean;
    savedFile?: SavedFile;
}

interface ILocalSavedFileItemActions {
    item: SavedFile;
    editItem?: (item: SavedFile) => void;
    deleteItem?: (item: SavedFile) => Promise<void>;
    disabled?: boolean;
    showUserAuthorization?: boolean;
    allowedActions?: FormAction[];
}

interface IUserFileAccess {
    parentLoading?: boolean;
    parentDisabled?: boolean;
    user: ApplicationUserSavedFileDto;
    item: SavedFile;
    reloadSingleRef?: MutableRefObject<
        | ((
        item: ApplicationUserSavedFileDto,
    ) => Promise<ApplicationUserSavedFileDto>)
        | undefined
    >;
}

const UserFileAccess: FC<IUserFileAccess> = ({
                                                 parentLoading,
                                                 parentDisabled,
                                                 item,
                                                 user,
                                                 reloadSingleRef,
                                             }) => {
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const toggleAccess = useCallback(() => {
        setLoading();
        Axios.post(
            "/ApplicationUserSavedFiles/ToggleAccess",
            {},
            {params: {userId: user.id, fileId: item.id}},
        )
            .then(() =>
                reloadSingleRef?.current
                    ? reloadSingleRef?.current(user)
                    : new Promise(() => {
                    }),
            )
            .catch()
            .finally(setNotLoading);
    }, [item, reloadSingleRef, parentLoading, user]);

    return (
        <ListItemCheckbox
            loading={loading}
            disabled={parentLoading || loading || parentDisabled}
            onClick={() => toggleAccess()}
            showUnchecked
            checked={user.hasAccess}
            colored
        />
    );
};

const LocalSavedFileItemActions: FC<ILocalSavedFileItemActions> = ({
                                                                       item,
                                                                       editItem,
                                                                       deleteItem,
                                                                       disabled,
                                                                       showUserAuthorization,
                                                                       allowedActions,
                                                                   }) => {
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);
    const {t} = useTranslation();
    const reloadSingleRef =
        useRef<
            (
                item: ApplicationUserSavedFileDto,
            ) => Promise<ApplicationUserSavedFileDto>
        >();

    const fileUsersColumns = useMemo(
        () =>
            MakeTenantColumns<ApplicationUserSavedFileDto>(
                {
                    userName: {
                        minWidth: 120,
                    },
                    firstName: {
                        maxWidth: 100,
                    },
                    lastName: {
                        maxWidth: 100,
                    },
                    hasAccess: {
                        maxWidth: 50,
                        render: (user) => (
                            <UserFileAccess
                                user={user}
                                item={item}
                                parentDisabled={false}
                                parentLoading={loading}
                                reloadSingleRef={reloadSingleRef}
                            />
                        ),
                    },
                },
                t,
            ),
        [t, loading, disabled, reloadSingleRef],
    );

    const makeApplicationUserSavedFileTable = useMemo(
        () => (
            <TenantList<
                    ApplicationUser,
                    ApplicationUserSavedFileDto,
                    ApplicationUserSavedFileDto
                >
                additionalIntParameter={item.id}
                title={t("users-with-access")}
                allowSearching
                style={{maxWidth: 600}}
                baseDto={{}}
                allowedActions={allowedActions}
                selectionModeOverride={SelectionMode.none}
                columns={fileUsersColumns}
                resource={TenantResource.ApplicationUserSavedFiles}
                reloadSingleRef={reloadSingleRef}
            />
        ),
        [item, fileUsersColumns, loading, disabled],
    );

    const makeActions = useMemo(() => {
        const actions: IContextualMenuItem[] = [];

        if (allowedActions?.includes("PUT") == true || !allowedActions) {
            actions.push({
                key: "edit",
                text: t("edit"),
                onClick: () => {
                    if (editItem) {
                        editItem?.(item);
                    }
                },
                iconProps: {iconName: "Edit"},
                disabled: loading,
            });
        }

        if (allowedActions?.includes("DELETE") == true || !allowedActions) {
            actions.push({
                key: "delete",
                text: t("delete"),
                onClick: () => {
                    deleteItem?.(item).finally(setNotLoading);
                },
                iconProps: {iconName: "Delete"},
                disabled: loading,
            });
        }

        if (showUserAuthorization) {
            actions.push({
                key: "users",
                text: t("users"),
                subMenuProps: {
                    items: [
                        {
                            key: "users",
                            text: t("Users"),
                            onRender: () => makeApplicationUserSavedFileTable,
                        },
                    ],
                },
                iconProps: {iconName: "People"},
                disabled: loading,
            });
        }

        return actions;
    }, [
        allowedActions,
        loading,
        editItem,
        deleteItem,
        showUserAuthorization,
        makeApplicationUserSavedFileTable,
    ]);

    return (
        <div>
            <Stack horizontal horizontalAlign="end" verticalAlign="center">
                <IconButton
                    disabled={loading}
                    iconProps={{iconName: "Download"}}
                    onClick={() => DownloadHelper(item)}
                />
                <IconButton
                    disabled={loading}
                    iconProps={{iconName: "OpenInNewWindow"}}
                    onClick={() => OpenInNewTabHelper(item)}
                />
                {makeActions.length && (
                    <IconButton
                        disabled={loading}
                        menuProps={{
                            items: makeActions,
                        }}
                    />
                )}
            </Stack>
        </div>
    );

    return (
        <Stack horizontal horizontalAlign="end" verticalAlign="center">
            {showUserAuthorization && (
                <OnixCallout
                    paddingLess
                    title={t("permissions")}
                    trigger={(id, show) => (
                        <IconButton
                            id={id}
                            disabled={disabled || loading}
                            iconProps={{iconName: "Contact"}}
                            onClick={(e) => show(e, undefined)}
                        />
                    )}
                >
                    {(show) => makeApplicationUserSavedFileTable}
                </OnixCallout>
            )}
            <IconButton
                disabled={disabled || loading}
                iconProps={{iconName: "Download"}}
                onClick={() => DownloadHelper(item)}
            />
            <IconButton
                disabled={disabled || loading}
                iconProps={{iconName: "OpenInNewWindow"}}
                onClick={() => OpenInNewTabHelper(item)}
            />
            <IconButton
                disabled={disabled || loading}
                iconProps={{iconName: "Edit"}}
                onClick={() => {
                    if (editItem) {
                        editItem(item);
                    }
                }}
            />
            <OnixCallout
                areYouSure={{
                    title: "you-want-to-delete-this-file",
                    onYes: () => {
                        if (deleteItem) {
                            setLoading();
                            deleteItem(item).finally(setNotLoading);
                        }
                    },
                }}
                trigger={(id, show) => (
                    <IconButton
                        style={{color: SharedColors.red10}}
                        id={id}
                        disabled={disabled || loading}
                        iconProps={{iconName: "Trash"}}
                        onClick={(e) => show(e, undefined)}
                    />
                )}
            />
        </Stack>
    );
};

export const FileUploadComponent: FC<
    PropsWithChildren<IFileUploadComponent>
> = ({
         query,
         className,
         allowedActions,
         filesListIsEmpty: allFilesRemoved,
         showUserAuthorization,
         baseDto,
         uniqueClientId,
         additionalDirectorySegments,
         children,
         keepOriginalExtension,
         maxXorY,
         nameOverride,
         prependOriginalFileName,
         translationKey,
         showDocumentList,
         hideTopBar,
         loadedCallback,
         loadingCallback,
         disabled,
         title,
         additionalCommands,
         selectionChanged,
         filesChanged,
         checkAgainstEmailMaxSize
     }) => {
    const {t} = useTranslation();
    const [filesForUpload, setFilesForUpload] = useState<FileWrapper[]>([]);
    const [, setLocalUploadedFiles] = useState<SavedFile[]>([]);
    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFilesForUpload((f) =>
            f.concat(
                acceptedFiles.map((m: any) => {
                    m.id = UUIDHelper();
                    return m;
                }),
            ),
        );
    }, []);
    // const [q, setQ] = useState<ITenantResourceQuery<SavedFile>>();

    // useEffect(() => {
    //     if (!q || !_.isEqual(q, query)) {
    //         setQ(query);
    //     }
    // }, [query, q]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        acceptedFiles,
        inputRef,
        rootRef,
    } = useDropzone({onDrop, noDragEventsBubbling: true, disabled: disabled});
    const reloadRef = useRef<() => Promise<PagedResult<SavedFile>>>();
    const editItemRef = useRef<(item: SavedFile) => void>();
    const deleteItemRef = useRef<(item: SavedFile) => Promise<void>>();

    const baseStyle: CSSProperties = useMemo(
        () => ({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            // minWidth: 500,
            alignItems: "center",
            padding: "10px 20px",
            borderWidth: 1,
            borderRadius: 2,
            borderColor: "rgb(133, 133, 133)",
            borderStyle: "solid",
            backgroundColor: "rgb(250, 250, 250)",
            color: "rgb(136, 136, 136)",
            outline: "none",
            transition: "all 0.2s ease-in-out",
        }),
        [],
    );

    const activeStyle: CSSProperties = useMemo(
        () => ({
            borderColor: theme?.palette.themePrimary,
            boxShadow: "0 0 2px " + theme?.palette.themePrimary,
        }),
        [],
    );

    const acceptStyle: CSSProperties = useMemo(
        () => ({
            borderColor: SharedColors.green10,
            boxShadow: "0 0 2px " + SharedColors.green10,
        }),
        [],
    );

    const rejectStyle: CSSProperties = useMemo(
        () => ({
            borderColor: SharedColors.red10,
            boxShadow: "0 0 2px " + SharedColors.red10,
        }),
        [],
    );

    const columns = useMemo(
        () =>
            MakeTenantColumns<SavedFile>(
                {
                    name: {
                        render: (item) => (
                            <Stack
                                horizontal
                                horizontalAlign="space-between"
                                style={{width: "100%"}}
                                tokens={{childrenGap: 5}}
                            >
                                <Stack style={{width: "100%"}}>
                                    <Text
                                        style={{
                                            whiteSpace: "break-spaces",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {item.name}
                                    </Text>
                                    <Text
                                        style={{whiteSpace: "break-spaces"}}
                                        variant={"xSmall"}
                                    >
                                        {item.description}
                                    </Text>
                                </Stack>
                                <LocalSavedFileItemActions
                                    allowedActions={allowedActions}
                                    showUserAuthorization={showUserAuthorization}
                                    disabled={disabled}
                                    item={item}
                                    editItem={editItemRef?.current}
                                    deleteItem={deleteItemRef?.current}
                                />
                            </Stack>
                        ),
                    },
                },
                t,
            ),
        [editItemRef, deleteItemRef, disabled, showUserAuthorization, t],
    );

    const form = useMemo<ITenantFormLayout<SavedFile>>(
        () => ({
            name: {
                type: new TitleElement({}),
            },
            description: {
                type: new MultilineTextElement({
                    rows: 5,
                }),
            },
        }),
        [],
    );

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragActive, isDragReject, isDragAccept],
    );

    const updateLocalUploadedFiles = useCallback(
        (updater: (files: SavedFile[]) => SavedFile[]) => {
            setLocalUploadedFiles((files) => {
                const nextFiles = updater(files);
                if (!showDocumentList) {
                    filesChanged?.(nextFiles);
                }

                return nextFiles;
            });
        },
        [filesChanged, showDocumentList],
    );

    const fileRemoved = useCallback((file: FileWrapper) => {
        setFilesForUpload((files) => files.filter((f) => f.id != file.id));

        if (file.savedFile) {
            updateLocalUploadedFiles((files) =>
                files.filter((savedFile) => savedFile.id !== file.savedFile?.id),
            );
        }
    }, [updateLocalUploadedFiles]);

    const reloadTimeout = useRef<number>();

    const reloadThrottled = useCallback(() => {
        if (reloadTimeout.current) clearTimeout(reloadTimeout.current);
        reloadTimeout.current = window.setTimeout(() => {
            if (showDocumentList) {
                if (reloadRef.current) {
                    reloadRef.current();
                }
            }
        }, 300);
    }, [showDocumentList, reloadTimeout, reloadRef]);

    const fileUploaded = useCallback(
        (file: FileWrapper, savedFile: SavedFile) => {
            if (!showDocumentList) {
                updateLocalUploadedFiles((files) => [
                    ...files.filter((existingFile) => existingFile.id !== savedFile.id),
                    savedFile,
                ]);
            }

            console.log(file);
            if (showDocumentList) {
                fileRemoved(file);
                reloadThrottled();
            }
        },
        [fileRemoved, reloadThrottled, showDocumentList, updateLocalUploadedFiles],
    );

    const fileChanged = useCallback((file: FileWrapper) => {
        setFilesForUpload((files) =>
            files.map((m) => (m.id == file.id ? file : m)),
        );
    }, []);

    useEffect(() => {
        if (filesForUpload.find((f) => f.loading || f.deleting)) {
            if (loadingCallback) loadingCallback();
        } else if (filesForUpload.every((e) => e.loaded)) {
            if (loadedCallback) loadedCallback();
        }
    }, [filesForUpload]);

    return (
        <Stack className={className}>
            {showDocumentList && (
                <TenantList<SavedFile, SavedFile, SavedFile>
                    baseDto={baseDto}
                    columns={columns}
                    resource={TenantResource.SavedFile}
                    query={query}
                    reloadRef={reloadRef}
                    queryIsRequired
                    hideTopBar={hideTopBar}
                    title={title}
                    allowedActions={allowedActions ?? ["PUT", "DELETE"]}
                    addingTitle={"adding-file"}
                    editingTitle={"editing-file"}
                    form={form}
                    selectionChanged={selectionChanged}
                    editItemRef={editItemRef}
                    deleteItemRef={deleteItemRef}
                    resultIsEmpty={allFilesRemoved}
                    resultsChanged={filesChanged}
                    hideNoItemsFooter
                    hideIfEmpty
                    additionalCommands={additionalCommands}
                />
            )}
            {!allowedActions || allowedActions.includes("POST") ? (
                <div style={{paddingTop: 5}}>
                    <div {...getRootProps({style})}>
                        <Stack style={{width: "100%"}} tokens={{childrenGap: 10}}>
                            {filesForUpload.map((file) => (
                                <FileForUploadElement
                                    disabled={disabled}
                                    fileChanged={fileChanged}
                                    key={file.name}
                                    file={file}
                                    removed={fileRemoved}
                                    uploaded={fileUploaded}
                                    checkAgainstEmailMaxSize={checkAgainstEmailMaxSize}
                                    baseDto={baseDto}
                                    maxDim={maxXorY}
                                    nameOverride={nameOverride}
                                    prependOriginalFileName={prependOriginalFileName}
                                    keepOriginalExtension={keepOriginalExtension}
                                    additionalDirectorySegments={additionalDirectorySegments}
                                />
                            ))}
                        </Stack>
                        <input ref={inputRef} {...getInputProps()} disabled={disabled}/>
                        {isDragActive ? (
                            <p>{t("drop-files-for-upload")}</p>
                        ) : (
                            <p>{t("drop-some-files-or-click-to-select")}</p>
                        )}
                    </div>
                </div>
            ) : (
                <Stack
                    style={{width: "100%", minWidth: "30vw"}}
                    styles={{root: {width: "100%", minWidth: "100%"}}}
                />
            )}
        </Stack>
    );
};

interface IFileForUploadElement {
    file: FileWrapper;
    removed: (file: FileWrapper) => void;
    uploaded: (file: FileWrapper, savedFile: SavedFile) => void;
    baseDto: Partial<SavedFile>;
    fileChanged: (file: FileWrapper) => void;
    disabled?: boolean;
    maxDim?: number;
    nameOverride?: string;
    prependOriginalFileName?: boolean;
    keepOriginalExtension?: boolean;
    additionalDirectorySegments?: string;
    checkAgainstEmailMaxSize?: boolean;
}

const FileForUploadElement: FC<PropsWithChildren<IFileForUploadElement>> = ({
                                                                                file,
                                                                                removed,
                                                                                uploaded,
                                                                                baseDto,
                                                                                fileChanged,
                                                                                disabled,
                                                                                additionalDirectorySegments,
                                                                                children,
                                                                                keepOriginalExtension,
                                                                                maxDim,
                                                                                nameOverride,
                                                                                prependOriginalFileName,
                                                                                checkAgainstEmailMaxSize
                                                                            }) => {
    const [errors, setErrors] = useState<any>();
    const {t} = useTranslation();
    const [uploadPercentage, setUploadPercentage] = useState<number>(0);
    const cancelToken = useRef<CancelTokenSource>(Axios.CancelToken.source());
    const [savedFile, setSavedFile] = useState<SavedFile>();

    const removeFile = useCallback(
        (e: any) => {
            if (e) e.stopPropagation();

            cancelToken.current.cancel();
            if (savedFile) {
                file.deleting = true;
                fileChanged(file);
                Axios.delete("SavedFile/" + savedFile.id)
                    .then(() => removed(file))
                    .catch(({data}) => {
                            setErrors(
                                ShowApiError(
                                    data?.response?.error ??
                                    data?.response ??
                                    data ??
                                    "Unknown error",
                                ),
                            );
                        },
                    )
                    .finally(() => {
                        file.deleting = false;
                        fileChanged(file);
                    });
            } else {
                removed(file);
            }
        },
        [file, cancelToken, savedFile],
    );

    const onUploadProgress = useCallback((progressEvent: any) => {
        setUploadPercentage(progressEvent.loaded / progressEvent.total);
    }, []);

    useEffect(() => {
        file.loading = true;
        fileChanged(file);
        const formData = new FormData();
        formData.append("file", file, file.name);
        const baseConfig: AxiosRequestConfig = {
            onUploadProgress,
            cancelToken: cancelToken.current.token,
        };

        baseConfig.params = {
            maxDim: maxDim,
            nameOverride: nameOverride,
            prependOriginalFileName: prependOriginalFileName,
            keepOriginalExtension: keepOriginalExtension,
            additionalDirectorySegments: additionalDirectorySegments,
            checkAgainstEmailMaxSize: checkAgainstEmailMaxSize
        };

        if (baseDto) {
            const firstKey = Object.keys(baseDto)[0];
            baseConfig.params["savedFileProperty"] = firstKey;
            baseConfig.params["savedFilePropertyValue"] =
                `${(baseDto as any)[firstKey]}`;

            for (const key in baseDto) {
                baseConfig.params["savedFileProperty_" + key] =
                    `${(baseDto as any)[key]}`;
            }
        }

        Axios.post<SavedFile>("SavedFile/Upload", formData, baseConfig)
            .then(({data}) => {
                setSavedFile(data);
                file.savedFile = data;
                fileChanged(file);
                uploaded(file, data);
            })
            .catch((data) => {
                setErrors(
                    data?.response?.data?.message ??
                    GetApiError(data?.response?.error ?? data?.response ?? data) ??
                    "Unknown error",
                );
            })
            .finally(() => {
                file.loading = false;
                file.loaded = true;
                fileChanged(file);
            });
    }, []);

    return (
        <Stack
            horizontal
            horizontalAlign="space-between"
            tokens={{childrenGap: 10}}
        >
            <Stack
                tokens={{childrenGap: 0}}
                style={{flex: 1}}
                verticalAlign="center"
            >
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    style={{paddingRight: 5}}
                >
                    <Text style={{color: SharedColors.gray30, wordBreak: "break-all"}}>
                        {file.name}
                    </Text>
                    <Text style={{color: SharedColors.gray30}}>
                        {file.deleting
                            ? t("deleting")
                            : uploadPercentage != 1
                                ? t("uploading")
                                : file.loading
                                    ? t("saving")
                                    : null}
                    </Text>
                </Stack>
                {errors ? (
                    errors instanceof Object ? (
                        <ShowApiError errors={errors}/>
                    ) : (
                        <Text variant="small" style={{color: SharedColors.red10}}>
                            {errors?.message ? <ShowApiError errors={errors?.message}/>
                                : t(errors ?? "there-was-an-error")}
                        </Text>
                    )
                ) : (
                    <ProgressIndicator
                        styles={{itemProgress: {padding: "2px 0 0 0"}}}
                        percentComplete={uploadPercentage}
                    />
                )}
            </Stack>
            <Stack horizontal tokens={{childrenGap: 10}}>
                {(file.loading || file.deleting) && <Spinner/>}
                <IconButton
                    disabled={file.deleting || disabled}
                    iconProps={{iconName: "trash"}}
                    onClick={removeFile}
                />
            </Stack>
        </Stack>
    );
};
